import {
  Box,
  Typography,
  Grid,
  Card,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import {
  useForm,
  FormProvider,
  Controller,
  useFormContext,
  useFormState,
} from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { common, input } from "../../../i18n";
import InfoPopper from "../../../components/popper/InfoPopper";
import NumberFormatterUtils from "../../../utils/numberFormatterUtils";
import { BaselineSettingTable } from "../tables/BaselineSettingTable";
import { goalSettingService } from "../../../service/goal-setting.service";
import alertService from "../../../components/alert/alertService";
import { ApiError } from "../../../api/ApiError";
import { GoalSettingContext } from "../../../contexts/GoalSettingContext";
import { GoalPlanBaseline } from "../../../models/GoalSetting";
import { CustomSelect2 } from "../../../components/input/CustomSelect2";
import { NumericFormat } from "react-number-format";
import CustomDatePicker2 from "../../../components/input/CustomDatePicker2";

/** ตัดทศนิยม (ไม่ปัด) เหลือ d ตำแหน่ง แล้วคืนเป็น number */
const truncTo = (n: number, d = 2) => Math.trunc(n * 10 ** d) / 10 ** d;

declare global {
  interface Window {
    __RHF_BASELINE__?: any;
  }
}

type Props = {
  id?: string;
};

export const BaselineSettingSectionV2 = ({ id }: Props) => {
  const { t } = useTranslation([common, input], { nsMode: "fallback" });
  const parentForm = useFormContext();

  const {
    emissionOptions, // [{ year, emission }, ...]
    methodology, // "baseline" | "baseYear" | ...
    isEditMode,
    goalInfo, // ค่าที่โหลดมาเวลาแก้ไข
    setIsPlanFetched,
    setHasUserTriggered,
    hasUserTriggered,
  } = GoalSettingContext();

  // ฟอร์มภายใน section (เฉพาะ baseline)
  const form = useForm({
    defaultValues: {
      baseYear: null as number | null, // Base Year for BAU
      startYear: null as number | null, // AD ของปีเริ่มต้น
      targetYear: null as number | null,
      bauEmission: 0, // BAU Emissions (จาก emissionOptions)
      percentAnnualGrowthRate: 3, // % growth rate
      percentReduction: 40, // % reduction เป้าหมาย
      targetAbsoluteRate: true, // Absolute Rate toggle (ขวา)
      startAbsoluteRate: true, // Absolute Rate ของ growth (ซ้าย)
      goalNetEmission: 0, // เป้า net emission (จาก API)
      rows: [] as GoalPlanBaseline[],
    },
  });
  const { watch, setValue } = form;
  const values = watch();

  const [showSection, setShowSection] = useState(false);

  // แสดงเฉพาะตอนเลือกวิธี baseline และมี emission data
  useEffect(() => {
    setShowSection(methodology === "baseline" && emissionOptions.length > 0);
  }, [methodology, emissionOptions]);

  // ---------- Edit mode: เติมค่าเริ่มต้นจาก goalInfo ----------
  useEffect(() => {
    if (!(isEditMode && goalInfo && goalInfo.methodology === "baseline"))
      return;

    // ปรับ mapping ตามโครงสร้างจริงของ goalInfo
    setValue("baseYear", (goalInfo as any).baseYear ?? null, {
      shouldDirty: false,
    });
    setValue("startYear", goalInfo.startYear ?? null, { shouldDirty: false });
    setValue("targetYear", goalInfo.targetYear ?? null, { shouldDirty: false });

    setValue("bauEmission", (goalInfo as any).bauEmission ?? 0, {
      shouldDirty: false,
    });
    setValue(
      "percentAnnualGrowthRate",
      (goalInfo as any).percentAnnualGrowthRate ?? 3,
      { shouldDirty: false },
    );

    setValue("percentReduction", goalInfo.percentReduction ?? 40, {
      shouldDirty: false,
    });
    setValue("targetAbsoluteRate", goalInfo.targetAbsoluteRate ?? true, {
      shouldDirty: false,
    });
    setValue("startAbsoluteRate", (goalInfo as any).startAbsoluteRate ?? true, {
      shouldDirty: false,
    });

    setValue("goalNetEmission", goalInfo.goalNetEmission ?? 0, {
      shouldDirty: false,
    });
    setValue("rows", (goalInfo.plans as GoalPlanBaseline[]) ?? [], {
      shouldDirty: false,
    });
    setIsPlanFetched(true);
  }, [isEditMode, goalInfo]);

  // ---------- utils ----------
  const minYear = emissionOptions.length
    ? Math.min(...emissionOptions.map((o) => o.year))
    : 2000;
  const maxYear = emissionOptions.length
    ? Math.max(...emissionOptions.map((o) => o.year))
    : 2050;

  const generateTargetYears = (start: number) => {
    const years: number[] = [];
    const max = 2050;
    for (let y = start + 1; y <= max; y++) {
      if (y % 5 === 0) years.push(y);
    }
    return years;
  };

  const targetYearOptions = useMemo(
    () =>
      values.startYear
        ? generateTargetYears(values.startYear).map((y) => ({
            nameEN: String(y),
            nameTH: String(y),
            value: y,
          }))
        : [],
    [values.startYear],
  );

  // ---------- onChange: Base Year for BAU ----------
  const handleBauBaseYearChange = (date: dayjs.Dayjs | null) => {
    const year = date?.year() ?? null;
    if (!year) return;

    setHasUserTriggered(true);

    const found = emissionOptions.find((e) => e.year === year);
    if (found) {
      setValue("baseYear", year, { shouldDirty: true });
      setValue("bauEmission", found.emission);
      setValue("startYear", year + 1);
    } else {
      setValue("baseYear", null, { shouldDirty: true });
      setValue("bauEmission", 0);
      setValue("startYear", null);
    }
  };

  // startYear >= targetYear → ขยับ targetYear ให้มากกว่าด้วยปีที่หาร 5 ลงตัว
  useEffect(() => {
    if (
      values.startYear &&
      values.targetYear &&
      values.startYear >= values.targetYear
    ) {
      const nextTargets = generateTargetYears(values.startYear);
      setValue("targetYear", nextTargets[0] ?? null);
    }
  }, [values.startYear, values.targetYear]);

  // ---------- Auto-generate plan ----------
  // Not include edit
  useEffect(() => {
    const {
      baseYear,
      startYear,
      targetYear,
      percentReduction,
      percentAnnualGrowthRate,
      startAbsoluteRate,
      targetAbsoluteRate,
      bauEmission,
    } = values;

    if (!baseYear || !startYear || !targetYear) return;
    if (isEditMode && !hasUserTriggered) return; // Edit mode: รอ user เปลี่ยนค่าก่อน
    if (startYear >= targetYear) return;

    const fetch = async () => {
      try {
        // ✅ เตรียม payload หลัก
        const payLoad: any = {
          baseYear,
          startYear,
          targetYear,
          percentReduction,
          bauEmission,
          targetAbsoluteRate,
          percentAnnualGrowthRate,
          startAbsoluteRate: !!startAbsoluteRate,
        };

        // ✅ ถ้า absoluteRate ทั้งคู่เป็น true → ไม่ต้องส่ง plans
        if (!(targetAbsoluteRate === true && startAbsoluteRate === true)) {
          const allYears: number[] = [];
          for (let y = startYear; y <= targetYear; y++) {
            allYears.push(y);
          }

          const currentPlans = values.rows || [];

          // ✅ รวมข้อมูลของทุกปีในช่วง startYear → targetYear
          const filledPlans = allYears.map((year) => {
            const existing = currentPlans.find((p) => p.year === year);
            return {
              year,
              percentReduction: existing?.percentReduction ?? 0,
              percentAnnualGrowthRate: existing?.percentAnnualGrowthRate ?? 0,
            };
          });

          payLoad.plans = filledPlans;
        }

        alertService.showLoading();
        // console.log("payload trigger", payLoad);

        const res = await goalSettingService.generatePlanBaseline(payLoad);
        alertService.closeLoading();

        // ✅ อัปเดตตารางกับ goalNetEmission
        setValue("rows", res?.plans || [], { shouldDirty: false });
        if ((res as any)?.goalNetEmission != null) {
          setValue("goalNetEmission", (res as any).goalNetEmission, {
            shouldDirty: false,
          });
        }

        setIsPlanFetched(true);
      } catch (err) {
        alertService.closeLoading();
        console.error("generatePlanBaseline error:", err);
        if (!(err instanceof ApiError)) {
          await alertService.error(t("Error-Fetching-Plan"));
        }
      }
    };

    fetch();
  }, [
    values.baseYear,
    values.startYear,
    values.targetYear,
    // values.percentReduction,
    // values.percentAnnualGrowthRate,
    values.startAbsoluteRate,
    values.targetAbsoluteRate,
    values.bauEmission,
    isEditMode,
    hasUserTriggered,
    setIsPlanFetched,
    setValue,
    t,
  ]);

  // ---------- Manual Trigger onBlur (percent fields) ----------
  const handleBlurGeneratePlan = async () => {
    const {
      baseYear,
      startYear,
      targetYear,
      percentReduction,
      percentAnnualGrowthRate,
      startAbsoluteRate,
      targetAbsoluteRate,
      bauEmission,
    } = form.getValues();

    // ✅ validate เงื่อนไขขั้นต่ำก่อนยิง
    if (!baseYear || !startYear || !targetYear) return;
    if (startYear >= targetYear) return;

    try {
      const payload: any = {
        baseYear,
        startYear,
        targetYear,
        percentReduction,
        bauEmission,
        targetAbsoluteRate,
        percentAnnualGrowthRate,
        startAbsoluteRate: !!startAbsoluteRate,
      };

      // ✅ กรณีมี plans ให้ส่งด้วย
      if (!(targetAbsoluteRate === true && startAbsoluteRate === true)) {
        const allYears: number[] = [];
        for (let y = startYear; y <= targetYear; y++) allYears.push(y);

        const currentPlans = form.getValues("rows") || [];
        payload.plans = allYears.map((year) => {
          const existing = currentPlans.find((p: any) => p.year === year);
          return {
            year,
            percentReduction: existing?.percentReduction ?? 0,
            percentAnnualGrowthRate: existing?.percentAnnualGrowthRate ?? 0,
          };
        });
      }

      alertService.showLoading();
      const res = await goalSettingService.generatePlanBaseline(payload);
      alertService.closeLoading();

      // ✅ update form values
      if (res?.plans) form.setValue("rows", res.plans, { shouldDirty: false });
      if ((res as any)?.goalNetEmission != null) {
        form.setValue("goalNetEmission", (res as any).goalNetEmission, {
          shouldDirty: false,
        });
      }

      setIsPlanFetched(true);
    } catch (err) {
      alertService.closeLoading();
      console.error("handleBlurGeneratePlan error:", err);
      if (!(err instanceof ApiError)) {
        await alertService.error(t("Error-Fetching-Plan"));
      }
    }
  };

  const { isDirty } = useFormState({ control: form.control });

  useEffect(() => {
    window.__RHF_BASELINE__ = form;
    return () => {
      delete window.__RHF_BASELINE__;
    };
  }, [form]);

  if (!showSection) return null;

  return (
    <FormProvider {...form}>
      <Box mt={4} id={id}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          {t("Settings")}
        </Typography>

        <Grid container spacing={2}>
          {/* ---------- Left: Starting Year ---------- */}
          <Grid item xs={6} sx={{ display: "flex" }}>
            <Card sx={{ p: 2, flex: 1 }} variant="outlined">
              <Typography variant="h4" color="secondary" sx={{ mb: "20px" }}>
                {t("Starting-Year")}
                <InfoPopper text={t("Starting-Year")} />
              </Typography>

              {/* Base Year for BAU */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Base-Year-For-Bau")} *
                    <InfoPopper text={t("Base-Year-For-BAU-Description")} />
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <FormControl fullWidth>
                    <Controller
                      name="baseYear"
                      control={form.control}
                      render={({ field: { value } }) => (
                        <CustomDatePicker2
                          info=""
                          label=""
                          defaultDate={null}
                          showInfo={false}
                          value={value ? dayjs(`${value}-01-01`) : null}
                          handleOnChange={handleBauBaseYearChange}
                          required
                          views={["year"]}
                          format="YYYY"
                          minDate={dayjs(`${minYear}-01-01`)}
                          maxDate={dayjs(`${maxYear}-12-31`)}
                          availableYears={emissionOptions.map((e) => e.year)}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              {/* BAU Emissions */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Bau-Emission")} *
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    value={NumberFormatterUtils.numberFormat(
                      values.bauEmission || 0,
                    )}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" sx={{ color: "var(--dark)" }}>
                    {/* {t("tCO₂e")} */}
                    tCO<sub>2</sub>e
                  </Typography>
                </Grid>
              </Grid>

              {/* Annual Growth Rate + Absolute Rate */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Annual-Growth-Rate")} *
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <NumericFormat
                    value={values.percentAnnualGrowthRate ?? ""}
                    onValueChange={(vals) => {
                      setHasUserTriggered(true);
                      const raw = vals.floatValue ?? null;
                      if (raw !== null && raw >= 0 && raw <= 100) {
                        setValue("percentAnnualGrowthRate", raw, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    thousandSeparator=","
                    allowLeadingZeros
                    allowNegative={false}
                    decimalScale={2}
                    fixedDecimalScale
                    customInput={TextField}
                    onFocus={(e) => e.target.select()} // ✅ คลุมดำเมื่อคลิก
                    disabled={!values.startAbsoluteRate}
                    size="small"
                    fullWidth
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      return (
                        floatValue == null ||
                        (floatValue >= 0 && floatValue <= 100)
                      );
                    }}
                    onBlur={handleBlurGeneratePlan}
                    onKeyDown={(event) => event.stopPropagation()}
                    onKeyUp={(event: React.KeyboardEvent) => {
                      if (event.key === "Enter") {
                        (document.activeElement as HTMLElement).blur(); // ➡ trigger onBlur
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!values.startAbsoluteRate}
                        onChange={(e) => {
                          setValue("startAbsoluteRate", e.target.checked, {
                            shouldDirty: true,
                          });
                          setHasUserTriggered(true);
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" color={"var(--dark)"}>
                        {t("Absolute-Rate")}
                        {/* <InfoPopper text={t("Absolute-Rate-Description")} /> */}
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>

              {/* Starting Year (AD) */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Starting-Year")} *
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <FormControl fullWidth>
                    <Controller
                      name="startYear"
                      control={form.control}
                      render={({ field: { onChange, value } }) => (
                        <CustomDatePicker2
                          info=""
                          label=""
                          defaultDate={null}
                          showInfo={false}
                          value={value ? dayjs(`${value}-01-01`) : null}
                          handleOnChange={(d) => {
                            setHasUserTriggered(true);
                            onChange(d?.year() ?? null);
                          }}
                          required
                          views={["year"]}
                          format="YYYY"
                          minDate={
                            values.baseYear
                              ? dayjs(`${values.baseYear + 1}-01-01`)
                              : dayjs("2000-01-01")
                          }
                          maxDate={dayjs("2049-12-31")}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* ---------- Right: Target Goal ---------- */}
          <Grid item xs={6} sx={{ display: "flex" }}>
            <Card sx={{ p: 2, flex: 1 }} variant="outlined">
              <Typography variant="h4" color="secondary" sx={{ mb: "20px" }}>
                {t("Target-Goal")}
              </Typography>

              {/* Target Year */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Target-Year")} *{" "}
                    <InfoPopper text={t("Target-Year-Description")} />
                  </Typography>
                </Grid>
                <Grid item xs={9}>
                  <CustomSelect2
                    name="targetYear"
                    control={form.control}
                    label={t("Target-Year")}
                    info=""
                    placeholder={t("Select")}
                    rules={{
                      onChange(event) {
                        setValue("targetYear", Number(event.target.value), {
                          shouldDirty: true,
                        });
                        setHasUserTriggered(true);
                      },
                    }}
                    required
                    listMenuItem={targetYearOptions}
                    showLabelInput={false}
                  />
                </Grid>
              </Grid>
              {/* Goal Net Emissions */}
              <Grid
                container
                alignItems="center"
                spacing={2}
                sx={{ mb: "20px" }}
              >
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    {t("Goal-Net-Emission")} *
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <NumericFormat
                    value={values.goalNetEmission}
                    thousandSeparator=","
                    decimalScale={2}
                    fixedDecimalScale
                    displayType="input" // ใช้ input เพื่อให้ format ลงในช่อง
                    customInput={TextField}
                    fullWidth
                    size="small"
                    disabled
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" sx={{ color: "var(--dark)" }}>
                    {/* {t("tCO₂e")} */}
                    tCO<sub>2</sub>e
                  </Typography>
                </Grid>
              </Grid>
              {/* % Reduction + Absolute Rate */}
              <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={3}>
                  <Typography variant="body1" sx={{ color: "var(--gray1)" }}>
                    % {t("Reduction")} *
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <NumericFormat
                    value={values.percentReduction ?? ""}
                    onValueChange={(vals) => {
                      setHasUserTriggered(true);
                      const raw = vals.floatValue ?? null;
                      if (raw !== null && raw >= 0 && raw <= 100) {
                        setValue("percentReduction", truncTo(raw, 2), {
                          shouldDirty: true,
                        });
                      }
                    }}
                    thousandSeparator=","
                    allowLeadingZeros
                    allowNegative={false}
                    decimalScale={2}
                    fixedDecimalScale
                    customInput={TextField}
                    disabled={!values.targetAbsoluteRate}
                    size="small"
                    fullWidth
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      return (
                        floatValue == null ||
                        (floatValue >= 0 && floatValue <= 100)
                      );
                    }}
                    onBlur={handleBlurGeneratePlan}
                    onKeyDown={(event) => event.stopPropagation()}
                    onKeyUp={(event: React.KeyboardEvent) => {
                      if (event.key === "Enter") {
                        (document.activeElement as HTMLElement).blur(); // ➡ trigger onBlur
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!values.targetAbsoluteRate}
                        onChange={(e) => {
                          setValue("targetAbsoluteRate", e.target.checked, {
                            shouldDirty: true,
                          });
                          setHasUserTriggered(true);
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" color={"var(--dark)"}>
                        {t("Absolute-Rate")}
                        <InfoPopper text={t("Absolute-Rate-Description")} />
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {/* ตาราง */}
        <Box mt={3}>
          {/* {JSON.stringify(watch("rows"))} */}
          <BaselineSettingTable />
        </Box>
      </Box>
    </FormProvider>
  );
};
